import React, { createContext, useContext, useState, useEffect } from "react";
import { toastError, toastInfo, toastSuccess } from "@/components/ui/toast";
import {
  doc,
  setDoc,
  serverTimestamp,
  collection,
  getDocs,
  deleteDoc,
  onSnapshot,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { useAuth } from "./AuthContext";
import { db } from "../firebase/client";
import { CartItem } from "@/lib/types";

// Create cart context types
interface CartContextType {
  addItem: (item: CartItem) => void;
  removeItem: (id: string) => void;
  updateItem: (id: string, updates: Partial<CartItem>) => void;
  clearCart: () => void;
  isInCart: (id: string) => boolean;
  getItemCount: () => number;
  isLoading: boolean;
  // cartDetails will be derived from typedCartItems
  cartDetails: Record<string, CartItem> | undefined;
  // setItemQuantity will directly call updateItem
  setItemQuantity: (id: string, quantity: number) => void;
  totalPrice: number | undefined; // Will be derived
  cartCount: number | undefined; // Will be derived
  typedCartItems: CartItem[];
}

const CartContext = createContext<CartContextType | undefined>(undefined);

// Wrapper component, no longer uses ShoppingCartProvider
export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // ShoppingCartProvider removed
  return <CartImplementation>{children}</CartImplementation>;
};

const CartImplementation: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Removed useShoppingCart hook and its destructured members

  const { userData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [firestoreCartLoaded, setFirestoreCartLoaded] = useState(false);
  const [typedCartItems, setTypedCartItems] = useState<CartItem[]>([]);

  // Derived state for cartCount and totalPrice will be added here later
  // For now, providing placeholders
  const [derivedCartCount, setDerivedCartCount] = useState<number>(0);
  const [derivedTotalPrice, setDerivedTotalPrice] = useState<number>(0);
  const [derivedCartDetails, setDerivedCartDetails] = useState<
    Record<string, CartItem> | undefined
  >(undefined);

  // Effect to derive cartCount, totalPrice, and cartDetails from typedCartItems
  useEffect(() => {
    const count = typedCartItems.reduce((sum, item) => sum + item.quantity, 0);
    setDerivedCartCount(count);

    const price = typedCartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    setDerivedTotalPrice(price);

    const details = typedCartItems.reduce(
      (acc, item) => {
        acc[item.id] = item;
        return acc;
      },
      {} as Record<string, CartItem>
    );
    setDerivedCartDetails(details);
  }, [typedCartItems]);

  // Load cart data from Firestore when user logs in
  useEffect(() => {
    if (!userData?.uid) {
      setFirestoreCartLoaded(false);
      setTypedCartItems([]);
      return;
    }

    setLoading(true);
    // No need to clearShoppingCart() from useShoppingCart anymore

    const cartCollection = collection(db, "users", userData.uid, "cart");
    const unsubscribe = onSnapshot(
      cartCollection,
      snapshot => {
        const newCartItems: CartItem[] = snapshot.docs.map(doc => {
          const data = doc.data();
          // Ensure createdAt is handled correctly (it's a Firestore Timestamp)
          return {
            id: data.id,
            projectId: data.projectId || "",
            generationId: data.generationId || "",
            quantity: data.quantity || 1,
            price: data.price || 0,
            displayName: data.displayName || "Product",
            userDescription: data.userDescription || "",
            imageUrl: data.imageUrl || data.image || "",
            stripePriceId: data.stripePriceId || "",
            printifyProductId: data.printifyProductId || "",
            printifyVariantId: data.printifyVariantId,
            stripeProductId: data.stripeProductId,
            shippingFirst: data.shippingFirst,
            shippingEach: data.shippingEach,
            createdAt: data.createdAt?.toDate?.() || Timestamp.now(),
          };
        });

        setTypedCartItems(newCartItems);
        if (!firestoreCartLoaded) {
          setFirestoreCartLoaded(true);
        }
        setLoading(false);
      },
      error => {
        console.error("Error loading cart from Firestore:", error);
        setLoading(false);
        toastError({
          title: "Cart Load Failed",
          description: "Failed to load your cart",
        });
      }
    );

    return () => unsubscribe();
  }, [userData?.uid, firestoreCartLoaded]); // Added firestoreCartLoaded to prevent re-runs after initial load if not needed

  const addItem = async (item: CartItem) => {
    if (!userData) {
      toastError({
        title: "Authentication Required",
        description: "Please sign in to add items to your cart",
      });
      return;
    }
    setLoading(true);
    try {
      if (
        !item.stripePriceId ||
        typeof item.stripePriceId !== "string" ||
        item.stripePriceId.trim() === ""
      ) {
        console.error("Invalid price ID for cart item:", item);
        throw new Error("Invalid price ID for cart item");
      }
      const firestoreItem = Object.entries(item).reduce(
        (acc, [key, value]) => {
          if (value !== undefined) acc[key] = value;
          return acc;
        },
        {} as Record<string, any>
      );
      const cartRef = doc(db, "users", userData.uid, "cart", item.id);
      await setDoc(cartRef, {
        ...firestoreItem,
        userId: userData.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      toastSuccess({
        title: "Item Added",
        description: "Item has been added to your cart",
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to add item to cart";
      toastError({ title: "Add to Cart Failed", description: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (id: string) => {
    if (!userData) {
      toastError({
        title: "Authentication Required",
        description: "Please sign in to manage your cart",
      });
      return;
    }
    try {
      const cartRef = doc(db, "users", userData.uid, "cart", id);
      await deleteDoc(cartRef);
      toastInfo({
        title: "Item Removed",
        description: "Item has been removed from your cart",
      });
    } catch (error) {
      toastError({
        title: "Remove Failed",
        description: "Failed to remove item from cart. Please try again.",
      });
    }
  };

  const updateItem = async (id: string, updates: Partial<CartItem>) => {
    if (!userData) {
      toastError({
        title: "Authentication Required",
        description: "Please sign in to manage your cart",
      });
      return;
    }
    try {
      const cartRef = doc(db, "users", userData.uid, "cart", id);
      const updatedItem = { ...updates, updatedAt: serverTimestamp() };
      await updateDoc(cartRef, updatedItem);
      // Firestore listener will update typedCartItems
    } catch (error) {
      toastError({
        title: "Update Failed",
        description: "Failed to update cart item. Please try again.",
      });
    }
  };

  const setItemQuantity = async (id: string, quantity: number) => {
    // No updateCartQuantity from useShoppingCart needed
    // This now directly calls our updateItem which updates Firestore
    await updateItem(id, { quantity });
  };

  const clearCart = async () => {
    if (!userData) {
      toastError({
        title: "Authentication Required",
        description: "Please sign in to manage your cart",
      });
      return;
    }
    setLoading(true);
    try {
      const cartCollection = collection(db, "users", userData.uid, "cart");
      const cartSnapshot = await getDocs(cartCollection);
      const deletePromises = cartSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
    } catch (error) {
      toastError({
        title: "Clear Failed",
        description: "Failed to clear cart. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const isInCart = (id: string) => {
    return typedCartItems.some(item => item.id === id);
  };

  const getItemCount = () => {
    return derivedCartCount; // Use derived count
  };

  const value = {
    addItem,
    removeItem,
    updateItem,
    clearCart,
    isInCart,
    getItemCount,
    isLoading: loading,
    cartDetails: derivedCartDetails, // Use derived details
    setItemQuantity,
    totalPrice: derivedTotalPrice, // Use derived price
    cartCount: derivedCartCount, // Use derived count
    typedCartItems,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
